import React, { useState, useMemo } from "react";
import {
  ShieldCheck,
  Pause,
  Play,
  RotateCw,
  Trash2,
  ExternalLink,
  Plus,
  Search,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Filter,
  Eye,
  Globe,
  Radio,
  Building,
  ChevronRight
} from "lucide-react";
import type { WatchlistItem } from "../types/domainMonitor";
import { toast } from "sonner";
import { toggleWatchlistStatus, rescanMonitoredDomain, removeFromWatchlist } from "../api/domainMonitor";

interface WatchlistControlPanelProps {
  watchlist: WatchlistItem[];
  onSelectDomain: (domain: string) => void;
  onOpenAddModal: () => void;
  onRefreshList: () => void;
}

export const WatchlistControlPanel: React.FC<WatchlistControlPanelProps> = ({
  watchlist,
  onSelectDomain,
  onOpenAddModal,
  onRefreshList,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "paused">("all");
  const [sortBy, setSortBy] = useState<"threats" | "recent" | "alpha">("threats");
  const [actionLoadingDomain, setActionLoadingDomain] = useState<string | null>(null);
  const [domainToDelete, setDomainToDelete] = useState<string | null>(null);

  // Compute Metrics
  const totalCount = watchlist.length;
  const activeCount = watchlist.filter((w) => (w.status || "active").toLowerCase() === "active").length;
  const pausedCount = watchlist.filter((w) => (w.status || "").toLowerCase() === "paused").length;
  const totalThreats = watchlist.reduce((acc, w) => acc + (w.hits ?? w.last_match_count ?? 0), 0);

  // Filter & Sort
  const filteredList = useMemo(() => {
    let list = [...watchlist];

    // Status filter
    if (statusFilter === "active") {
      list = list.filter((w) => (w.status || "active").toLowerCase() === "active");
    } else if (statusFilter === "paused") {
      list = list.filter((w) => (w.status || "").toLowerCase() === "paused");
    }

    // Search filter
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      list = list.filter(
        (w) =>
          (w.domain || "").toLowerCase().includes(q) ||
          (w.label || "").toLowerCase().includes(q)
      );
    }

    // Sort
    if (sortBy === "threats") {
      list.sort((a, b) => (b.hits ?? b.last_match_count ?? 0) - (a.hits ?? a.last_match_count ?? 0));
    } else if (sortBy === "recent") {
      list.sort(
        (a, b) =>
          new Date(b.last_scan_at || b.created_at || 0).getTime() -
          new Date(a.last_scan_at || a.created_at || 0).getTime()
      );
    } else if (sortBy === "alpha") {
      list.sort((a, b) => (a.domain || "").localeCompare(b.domain || ""));
    }

    return list;
  }, [watchlist, statusFilter, searchTerm, sortBy]);

  // Actions
  const handleToggleStatus = async (item: WatchlistItem) => {
    const isCurrentlyActive = (item.status || "active").toLowerCase() === "active";
    const nextStatus = isCurrentlyActive ? "paused" : "active";
    setActionLoadingDomain(item.domain);
    try {
      await toggleWatchlistStatus(item.domain, nextStatus);
      toast.success(
        nextStatus === "paused"
          ? `Monitoring paused for ${item.domain}`
          : `24/7 Monitoring resumed for ${item.domain}`
      );
      onRefreshList();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to update monitoring status");
    } finally {
      setActionLoadingDomain(null);
    }
  };

  const handleRescan = async (item: WatchlistItem) => {
    setActionLoadingDomain(item.domain);
    try {
      const res = await rescanMonitoredDomain(item.domain);
      toast.success(`Scanned ${item.domain}: ${res.item?.hits ?? 0} darknet exposures found`);
      onRefreshList();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to re-scan domain");
    } finally {
      setActionLoadingDomain(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!domainToDelete) return;
    setActionLoadingDomain(domainToDelete);
    try {
      await removeFromWatchlist(domainToDelete);
      toast.success(`Domain ${domainToDelete} removed from continuous monitoring`);
      setDomainToDelete(null);
      onRefreshList();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to remove domain");
    } finally {
      setActionLoadingDomain(null);
    }
  };

  const formatRelativeTime = (isoString?: string) => {
    if (!isoString) return "Never";
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) {
      // Future
      const inHours = Math.round(Math.abs(diffMs) / 3600000);
      return inHours <= 1 ? "in < 1 hr" : `in ${inHours} hrs`;
    }
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    return `${diffDays}d ago`;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "100%",
      }}
    >
      <style>{`
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.2); }
        }
        .pulse-live-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #10b981;
          display: inline-block;
          box-shadow: 0 0 8px #10b981;
          animation: pulseDot 2s infinite ease-in-out;
        }
      `}</style>

      {/* Metrics Banner */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "14px",
        }}
      >
        {/* Metric 1: Total */}
        <div
          style={{
            background: "#0f172a",
            border: "1px solid #1e293b",
            borderRadius: "10px",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
            Total Monitored
          </span>
          <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <span style={{ fontSize: "24px", fontWeight: 700, color: "#f8fafc" }}>
              {totalCount}
            </span>
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>assets</span>
          </div>
        </div>

        {/* Metric 2: Active 24/7 */}
        <div
          style={{
            background: "#0f172a",
            border: "1px solid #1e293b",
            borderRadius: "10px",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
              Continuous Active
            </span>
            <span className="pulse-live-dot" />
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <span style={{ fontSize: "24px", fontWeight: 700, color: "#10b981" }}>
              {activeCount}
            </span>
            <span style={{ fontSize: "12px", color: "#10b981" }}>24/7 surveillance</span>
          </div>
        </div>

        {/* Metric 3: Paused */}
        <div
          style={{
            background: "#0f172a",
            border: "1px solid #1e293b",
            borderRadius: "10px",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
            Paused
          </span>
          <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <span style={{ fontSize: "24px", fontWeight: 700, color: pausedCount > 0 ? "#f59e0b" : "#64748b" }}>
              {pausedCount}
            </span>
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>on standby</span>
          </div>
        </div>

        {/* Metric 4: Total Exposures */}
        <div
          style={{
            background: "#0f172a",
            border: "1px solid #1e293b",
            borderRadius: "10px",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
            Total Exposures Detected
          </span>
          <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <span style={{ fontSize: "24px", fontWeight: 700, color: totalThreats > 0 ? "#ef4444" : "#10b981" }}>
              {totalThreats}
            </span>
            <span style={{ fontSize: "12px", color: totalThreats > 0 ? "#ef4444" : "#10b981" }}>darknet matches</span>
          </div>
        </div>
      </div>

      {/* Control Bar: Search, Filters, Add Button */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          background: "#0f172a",
          border: "1px solid #1e293b",
          borderRadius: "10px",
          padding: "12px 16px",
        }}
      >
        {/* Search Input */}
        <div style={{ position: "relative", minWidth: "220px", flex: 1 }}>
          <Search
            size={14}
            style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#64748b" }}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search monitored domains..."
            style={{
              width: "100%",
              background: "#090d16",
              border: "1px solid #1e293b",
              borderRadius: "6px",
              padding: "7px 10px 7px 32px",
              fontSize: "12.5px",
              color: "#f8fafc",
              outline: "none",
            }}
          />
        </div>

        {/* Status Filter Tabs */}
        <div style={{ display: "flex", background: "#090d16", borderRadius: "6px", padding: "2px", border: "1px solid #1e293b" }}>
          {(["all", "active", "paused"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setStatusFilter(tab)}
              style={{
                background: statusFilter === tab ? "#1e293b" : "transparent",
                color: statusFilter === tab ? "#f8fafc" : "#64748b",
                border: "none",
                borderRadius: "4px",
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "capitalize",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Sort Select */}
        <select
          value={sortBy}
          onChange={(e: any) => setSortBy(e.target.value)}
          style={{
            background: "#090d16",
            border: "1px solid #1e293b",
            borderRadius: "6px",
            padding: "7px 10px",
            fontSize: "12px",
            color: "#cbd5e1",
            outline: "none",
            cursor: "pointer",
          }}
        >
          <option value="threats">Sort: Most Threats</option>
          <option value="recent">Sort: Recently Scanned</option>
          <option value="alpha">Sort: Domain (A-Z)</option>
        </select>

        {/* Add Domain Button */}
        <button
          onClick={onOpenAddModal}
          style={{
            background: "#10b981",
            color: "#022c22",
            border: "none",
            borderRadius: "6px",
            padding: "8px 16px",
            fontSize: "12.5px",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
          }}
        >
          <Plus size={14} strokeWidth={3} />
          <span>Add Domain</span>
        </button>
      </div>

      {/* Monitored Assets List */}
      {filteredList.length === 0 ? (
        <div
          style={{
            background: "#0f172a",
            border: "1px dashed #334155",
            borderRadius: "12px",
            padding: "48px 24px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              background: "rgba(16, 185, 129, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#10b981",
            }}
          >
            <Radio size={24} />
          </div>
          <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#f8fafc", margin: 0 }}>
            {searchTerm ? "No matching monitored domains" : "No domains currently monitored"}
          </h3>
          <p style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "420px", margin: 0, lineHeight: 1.5 }}>
            {searchTerm
              ? "Try adjusting your search keywords or clear the status filter."
              : "Enable continuous 24/7 dark web surveillance for your domains. We check forums, ransomware sites, and databases automatically every 24 hours."}
          </p>
          {!searchTerm && (
            <button
              onClick={onOpenAddModal}
              style={{
                marginTop: "8px",
                background: "#10b981",
                color: "#022c22",
                border: "none",
                borderRadius: "6px",
                padding: "8px 18px",
                fontSize: "13px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Add Your First Domain
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {filteredList.map((item) => {
            const hits = item.hits ?? item.last_match_count ?? 0;
            const isActive = (item.status || "active").toLowerCase() === "active";
            const isLoading = actionLoadingDomain === item.domain;

            const badgeBg = hits > 0 ? "rgba(239, 68, 68, 0.12)" : "rgba(16, 185, 129, 0.12)";
            const badgeColor = hits > 0 ? "#ef4444" : "#10b981";
            const badgeBorder = hits > 0 ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(16, 185, 129, 0.3)";

            return (
              <div
                key={item._id || item.domain}
                style={{
                  background: "#0f172a",
                  border: "1px solid #1e293b",
                  borderRadius: "10px",
                  padding: "16px 20px",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "16px",
                  transition: "border-color 0.15s ease",
                }}
              >
                {/* Domain & Identity Info */}
                <div style={{ display: "flex", alignItems: "center", gap: "14px", minWidth: "240px", flex: 2 }}>
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "8px",
                      background: "rgba(16, 185, 129, 0.08)",
                      border: "1px solid rgba(16, 185, 129, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#10b981",
                      flexShrink: 0,
                    }}
                  >
                    <Globe size={18} />
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span
                        onClick={() => onSelectDomain(item.domain)}
                        style={{
                          fontSize: "14.5px",
                          fontWeight: 700,
                          color: "#f8fafc",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        title="Click to load deep scan"
                      >
                        {item.domain}
                        <ExternalLink size={12} color="#64748b" />
                      </span>

                      {/* Status indicator */}
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          padding: "2px 7px",
                          borderRadius: "12px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          background: isActive ? "rgba(16, 185, 129, 0.15)" : "rgba(245, 158, 11, 0.15)",
                          color: isActive ? "#10b981" : "#f59e0b",
                          border: isActive ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid rgba(245, 158, 11, 0.3)",
                        }}
                      >
                        <span
                          style={{
                            width: "5px",
                            height: "5px",
                            borderRadius: "50%",
                            background: isActive ? "#10b981" : "#f59e0b",
                          }}
                        />
                        {isActive ? "Active 24/7" : "Paused"}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "4px" }}>
                      {item.label && item.label !== item.domain && (
                        <span style={{ fontSize: "11.5px", color: "#94a3b8", display: "flex", alignItems: "center", gap: "4px" }}>
                          <Building size={11} /> {item.label}
                        </span>
                      )}
                      <span style={{ fontSize: "11px", color: "#64748b" }}>
                        Cadence: {item.scan_cadence || "24h"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Threat Exposure Stats */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: "160px", flex: 1 }}>
                  <div
                    style={{
                      background: badgeBg,
                      color: badgeColor,
                      border: badgeBorder,
                      padding: "6px 12px",
                      borderRadius: "6px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      minWidth: "80px",
                    }}
                  >
                    <span style={{ fontSize: "15px", fontWeight: 800 }}>{hits}</span>
                    <span style={{ fontSize: "9.5px", fontWeight: 600, textTransform: "uppercase" }}>
                      {hits === 1 ? "Leak Hit" : "Leak Hits"}
                    </span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "2px", fontSize: "11px", color: "#64748b" }}>
                    <span>Last scan: <strong style={{ color: "#94a3b8" }}>{formatRelativeTime(item.last_scan_at || item.last_checked)}</strong></span>
                    <span>Next scan: <strong style={{ color: "#94a3b8" }}>{isActive ? formatRelativeTime(item.next_scan_at) : "Paused"}</strong></span>
                  </div>
                </div>

                {/* Operational Action Buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {/* View Results */}
                  <button
                    onClick={() => onSelectDomain(item.domain)}
                    style={{
                      background: "#1e293b",
                      color: "#f8fafc",
                      border: "1px solid #334155",
                      borderRadius: "6px",
                      padding: "6px 12px",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    title="View detailed darknet search report"
                  >
                    <Eye size={13} />
                    <span>View</span>
                  </button>

                  {/* Rescan On-Demand */}
                  <button
                    onClick={() => handleRescan(item)}
                    disabled={isLoading}
                    style={{
                      background: "#1e293b",
                      color: "#94a3b8",
                      border: "1px solid #334155",
                      borderRadius: "6px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    title="Trigger immediate re-scan"
                  >
                    <RotateCw size={13} className={isLoading ? "animate-spin" : ""} />
                    <span>Scan Now</span>
                  </button>

                  {/* Pause / Resume */}
                  <button
                    onClick={() => handleToggleStatus(item)}
                    disabled={isLoading}
                    style={{
                      background: isActive ? "rgba(245, 158, 11, 0.1)" : "rgba(16, 185, 129, 0.1)",
                      color: isActive ? "#f59e0b" : "#10b981",
                      border: isActive ? "1px solid rgba(245, 158, 11, 0.3)" : "1px solid rgba(16, 185, 129, 0.3)",
                      borderRadius: "6px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: isLoading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    title={isActive ? "Pause continuous surveillance" : "Resume continuous surveillance"}
                  >
                    {isActive ? <Pause size={13} /> : <Play size={13} />}
                    <span>{isActive ? "Pause" : "Resume"}</span>
                  </button>

                  {/* Remove with confirmation */}
                  <button
                    onClick={() => setDomainToDelete(item.domain)}
                    style={{
                      background: "transparent",
                      color: "#64748b",
                      border: "1px solid transparent",
                      borderRadius: "6px",
                      padding: "6px",
                      cursor: "pointer",
                    }}
                    title="Remove domain"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#ef4444";
                      e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#64748b";
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation Modal for Delete */}
      {domainToDelete && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "16px",
          }}
        >
          <div
            style={{
              background: "#0f172a",
              border: "1px solid #1e293b",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "420px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.6)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", color: "#ef4444" }}>
              <AlertTriangle size={24} />
              <h3 style={{ fontSize: "16px", fontWeight: 700, margin: 0, color: "#f8fafc" }}>
                Remove from Continuous Monitoring?
              </h3>
            </div>
            <p style={{ fontSize: "13px", color: "#94a3b8", margin: 0, lineHeight: 1.5 }}>
              Are you sure you want to stop monitoring <strong style={{ color: "#f8fafc" }}>{domainToDelete}</strong>?
              Automated 24-hour scans and real-time leak alerts will be deactivated for this asset.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "8px" }}>
              <button
                onClick={() => setDomainToDelete(null)}
                style={{
                  background: "transparent",
                  color: "#94a3b8",
                  border: "1px solid #334155",
                  borderRadius: "6px",
                  padding: "8px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Keep Monitored
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  background: "#ef4444",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "12px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Confirm Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
